import React from "react";
import { FormattedList, FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";

import { useModalContext } from "@/contexts/modal-context";
import { type Status } from "@/schemas";
import { getStatusMentions } from "@/utils/status";

import AccountPopover from "./popovers/account-popover";
import StatusPopover from "./popovers/status-popover";

interface StatusReplyMentionsProps {
  status: Status;
  hoverable?: boolean;
}

const StatusReplyMentions: React.FC<StatusReplyMentionsProps> = ({
  status,
  hoverable = true,
}) => {
  const { openModal } = useModalContext();

  const handleOpenMentionsModal: React.MouseEventHandler<HTMLSpanElement> = (
    e,
  ) => {
    e.stopPropagation();

    openModal("MENTIONS", { statusId: status.id });
  };

  if (!status.in_reply_to_id || !!status.in_reply_to?.prediction) {
    return null;
  }

  const to = getStatusMentions(status);

  // The post is a reply, but it has no mentions.
  // Rare, but it can happen.
  if (to.length === 0) {
    return (
      <div className="mb-1 block text-sm text-muted-foreground">
        <FormattedMessage
          id="reply_mentions.reply_empty"
          defaultMessage="Replying to Truth"
        />
      </div>
    );
  }

  // The typical case with a reply-to and a list of mentions.
  const accounts = to.slice(0, 2).map((account) => {
    const link = (
      <Link
        key={account.id}
        to={`/@${account.acct}`}
        className="inline-block max-w-[200px] truncate align-bottom text-primary-600 no-underline hover:text-primary-700 hover:underline dark:text-primary-400 dark:hover:text-primary-400"
        onClick={(e) => e.stopPropagation()}
      >
        @{account.username}
      </Link>
    );

    if (hoverable) {
      return (
        <AccountPopover key={account.id} account={account}>
          {link}
        </AccountPopover>
      );
    } else {
      return link;
    }
  });

  if (to.length > 2) {
    accounts.push(
      <span
        key="more"
        className="cursor-pointer hover:underline"
        role="button"
        onClick={handleOpenMentionsModal}
        tabIndex={0}
      >
        <FormattedMessage
          id="reply_mentions.more"
          defaultMessage="{count} more"
          values={{ count: to.length - 2 }}
        />
      </span>,
    );
  }

  return (
    <div className="mb-1 block text-sm text-muted-foreground">
      <FormattedMessage
        id="reply_mentions.reply.hoverable"
        defaultMessage="<hover>Replying to</hover> {accounts}"
        values={{
          accounts: <FormattedList type="conjunction" value={accounts} />,
          // @ts-ignore wtf?
          hover: (children: React.ReactNode) => {
            if (hoverable) {
              return (
                <StatusPopover status={{ id: status.in_reply_to_id! }}>
                  <span className="cursor-pointer">{children}</span>
                </StatusPopover>
              );
            } else {
              return children;
            }
          },
        }}
      />
    </div>
  );
};

export default StatusReplyMentions;
